/* ============================================================
   MagnuMaster — consent, analytics & advertising pixels
   ------------------------------------------------------------
   Loaded on every public page, before any other script. IDs live
   here only, so a change is a one-file edit rather than 24 edits.

   - Meta Pixel        794784032223260
   - Google tag        GT-NF7QHSSD — the account's one container tag; it feeds
                       GA4 G-03E28HFWXX and Ads AW-11102790094 from a single
                       config, plus a dead UA- tag that wants removing (below)
   - Microsoft Clarity xvllt9gzlx — session replay and heatmaps
   Domain verification (Meta) is a static <meta> tag in each page
   <head>, not here — Meta's crawler reads raw HTML, not injected
   tags.

   CONSENT (GDPR / EU ePrivacy)
   ---------------------------
   Two different mechanisms, because the two vendors are not the
   same kind of tracker:

   - Meta  — no script is injected at all until Accept is clicked, and
             then only if nothing else has already claimed the pixel.
             Meta has no cookieless mode; it is pure marketing, so
             it stays behind a hard opt-in gate.
             Shopify's own Meta channel pixel is governed by Shopify →
             Settings → Customer privacy, NOT by this banner. Verified
             on the live page 06.09.2026: with consent denied it loads
             nothing and writes no `_fbp`; on Accept it loads its own
             copy of fbevents.js and inits this same id. That is why
             bootMeta() below refuses to register a pixel twice.
   - Clarity — loads for everyone, like Google, so the traffic figures
             are the real ones rather than the accepted-only subset.
             Until Accept it runs with `clarity('consent', false)`,
             which is Clarity's own cookieless mode: no `_clck` or
             `_clsk` is written and nothing persists between visits.
             On Accept we send `clarity('consent')` and it upgrades to
             normal cookie-based measurement.

             This is a business decision taken deliberately: the
             client needs to see every visit. Note that it does mean
             session *recording* starts before a visitor has agreed to
             it, which is a stronger claim than Google's anonymous
             pings. If that is ever challenged, the fix is to move
             bootClarity() back behind the Accept branch — the queue
             machinery for that is still here and still works.
   - Google — loads for everyone under **Consent Mode v2** with all
             storage denied by default. In that state gtag sets no
             cookies and no identifiers; it sends anonymous, non-
             persistent pings so GA4 can still count traffic and
             model the rest. On Accept we send a `consent update`
             and it upgrades to normal cookie-based measurement.

   WHEN THEY LOAD
   --------------
   Google and Clarity are fetched on the visitor's **first interaction**,
   not while the page is painting — see the note beside onInteraction()
   below for the measurements and for exactly what each one costs. The
   short version: Google loses nothing (this container sends no page
   view, and its one event is a form submission), Clarity loses a visit
   that had no interaction at all. `CLARITY_ON_INTERACTION = false`
   reverts Clarity to booting with the page.

   That split is deliberate. Before it, a decline meant GA4 saw
   literally nothing, which is why GA4 reported a fraction of what
   Meta's ad-click "landing page views" reported.

   The choice is stored in localStorage under `mm_consent` for a
   year. Clearing that key re-opens the banner (handy for testing).

   DEBUGGING
   ---------
   Append to any URL — both persist for the browser tab (session),
   so they survive navigation across pages. `=0` turns them off.

     ?gadebug=1   sends GA4 hits flagged for **DebugView**
                  (GA4 → Admin → DebugView shows them live)
     ?mmdebug=1   traces consent state and every funnel event to
                  the browser console, prefixed [MM]

   FUNNEL API
   ----------
   window.MM_TRACK lets the rest of the site report the funnel
   without knowing any vendor API:

     MM_TRACK.viewContent(item)    product page opened
     MM_TRACK.addToCart(item)      item added to the cart
     MM_TRACK.beginCheckout(items) buyer sent to Shopify checkout
     MM_TRACK.lead(detail)         quote form submitted

   WHO REPORTS WHAT — read this before adding a vendor call
   --------------------------------------------------------
   The Shopify **Google & YouTube** and **Meta** sales channels are
   installed on the store, and their web pixels already report the
   whole commerce funnel — page_view, view_item, add_to_cart,
   begin_checkout and purchase — to this same Google tag and this
   same Meta pixel, largely from Shopify's own servers.

   So this file deliberately no longer sends those. Doing both counted
   every storefront event twice: Meta only de-duplicates when the two
   senders share an `event_id`, and two hits on one Google tag are
   simply two hits. Traffic read about double, and the funnel looked
   twice as leaky as it really is.

   The split that remains:

     Shopify's channels  page_view, view_item, add_to_cart,
                         begin_checkout, purchase — Google and Meta
     This file           generate_lead / Lead — the quote form, which
                         Shopify cannot see — plus every Clarity event
                         and the consent banner itself

   Clarity still receives all four MM_TRACK calls, as custom events
   plus an `mm_value` tag, so a replay can be filtered down to the
   sessions that actually converted. Shopify feeds Clarity nothing, so
   there is no duplication to undo there.

   Meta events raised before a decision are held in a small queue and
   flushed on Accept, so a Lead raised while the banner is still up is
   not lost. On Decline that queue is dropped.

   **Do not re-add a Google or Meta send to fire().** If the sales
   channels are ever removed, restore them here in the same change —
   but never while both exist. NEXT-STEPS.md §5 carries the history.
   ============================================================ */
(function () {
  'use strict';

  var META_ID = '794784032223260';
  var CLARITY_ID = 'xvllt9gzlx';
  /* The account's single Google tag. A `GT-` id is a container, not a
     property: the account owns the destination list, and ONE config call fans
     out to every entry in it. Read straight out of the live container on
     06.09.2026, that list is

       G-03E28HFWXX     GA4   "magnumaster – GA4"
       AW-11102790094   Google Ads
       UA-74960318-1    Universal Analytics — DEAD, and still being loaded

     Managed at Google Ads → Tools → Data manager → this tag → Destinations.
     Adding or removing a destination is therefore an account setting now, not
     a code change. Two consequences worth knowing:

     - `G-93VXQFP0XG`, the old MonsterInsights GA4 property, used to be on this
       list and no longer is. Anything that still describes it as a live
       destination is out of date.
     - `UA-74960318-1` is attached as a child container, so gtag.js fetches it
       on every page. Universal Analytics was switched off by Google in 2024,
       so it collects nothing at all — it is pure weight, and it cannot be
       removed from here. See NEXT-STEPS.md.

     Do NOT also config those three ids individually. They are already
     destinations of this tag, so a second config counts every hit twice —
     that is what Google means by "don't add more than one Google tag to each
     page". The site previously loaded G-03E28HFWXX on its own, which is why
     Google reported the tag as "not detected" and why only one of the two GA4
     properties ever filled up. */
  var GOOGLE_TAG = 'GT-NF7QHSSD';
  var CUR = 'EUR';

  /* Cross-domain measurement. Checkout leaves this site for Shopify, so
     without this the buyer arrives as a brand-new user from a referral and
     the sale is attributed to "magnumaster.at" instead of the ad.
     NOTE: this half only decorates outgoing links. The same list must also
     be entered in GA4 → Admin → Data streams → Configure tag settings →
     Configure your domains, or GA4 ignores the incoming _gl parameter. */
  var LINKED_DOMAINS = [
    'magnumaster.at',
    'www.magnumaster.at',
    'magnumaster.myshopify.com',
    'shopify.com',
    'shop.app'
  ];

  var KEY = 'mm_consent';
  var TTL = 365 * 24 * 60 * 60 * 1000;   // re-ask after a year

  var metaBooted = false;
  var metaQueue = [];
  var clarityBooted = false;
  var clarityQueue = [];

  /* ---- when the vendor scripts start ---------------------------------------
     Both of these used to be fetched and executed while the page was still
     painting. On a fast machine that is invisible; on the kind of CPU a
     real visitor has, Lighthouse measured 688 ms of main-thread time for
     gtag.js and 426 ms for Clarity — more, between them, than everything
     Shopify injects. That is time the page cannot respond to a tap.

     So they wait for the visitor's first interaction instead. What that
     costs is not the same for the two vendors, and the difference matters:

       - GOOGLE loses nothing at all. Our container sends `send_page_view:
         false` and Shopify's Google channel reports every page view, every
         view_item, add_to_cart and begin_checkout. The one event this
         container sends is `generate_lead`, on the quote form — a form
         submission cannot happen before an interaction. And `gtag()` is a
         dataLayer push, so the consent defaults set above still queue in
         order and are read by gtag.js whenever it arrives.

       - CLARITY records the session, so a visit with *no* interaction at
         all is not recorded. The trigger list below is deliberately wide —
         a single mouse move or one pixel of scroll is enough — so a human
         is caught almost immediately, but a bot that loads the page and
         leaves is not. If you would rather have that visit than the
         milliseconds, set CLARITY_ON_INTERACTION to false: Clarity goes
         back to booting with the page and nothing else changes.

     It ships false, deliberately. "Record every visit" is a decision the
     client has already taken once (see the CONSENT note at the top) and it
     outranks the ~110 ms this would save on their traffic. Google is gated
     because gating it costs nothing; Clarity is not, because it does.
     Flip it only if someone decides that trade differently. */
  var CLARITY_ON_INTERACTION = false;

  var interacted = false;
  var interactionQueue = [];
  // pointer/keyboard/touch/scroll cover a real visitor; visibilitychange and
  // pagehide are a last chance for someone leaving without touching anything.
  var WAKE_EVENTS = ['pointerdown', 'pointermove', 'mousemove', 'wheel',
                     'touchstart', 'keydown', 'scroll', 'visibilitychange', 'pagehide'];

  function wake(e) {
    if (interacted) return;
    // visibilitychange fires on the way *in* as well as out — a tab restored
    // from the background, or a headless browser that starts hidden. Only the
    // hidden direction means "this visitor is leaving".
    if (e && e.type === 'visibilitychange' && document.visibilityState !== 'hidden') return;
    interacted = true;
    for (var i = 0; i < WAKE_EVENTS.length; i++) {
      window.removeEventListener(WAKE_EVENTS[i], wake, true);
    }
    var held = interactionQueue.splice(0, interactionQueue.length);
    held.forEach(function (fn) { try { fn(); } catch (e) { } });
  }
  for (var wi = 0; wi < WAKE_EVENTS.length; wi++) {
    // passive + capture: never delay a scroll, and see the event before any
    // handler on the page can stop it propagating.
    window.addEventListener(WAKE_EVENTS[wi], wake, { passive: true, capture: true });
  }

  /* Run fn on the first interaction — or right now if one already happened,
     or if something needs to send an event before the visitor got round to
     it (the quote form does exactly that). */
  function onInteraction(fn) {
    if (interacted) { try { fn(); } catch (e) { } return; }
    interactionQueue.push(fn);
  }

  /* Run fn once the page has finished loading and the main thread is free.

     This is where Clarity goes, and it is the whole reason its numbers can
     stay complete without costing the largest paint. Clarity takes a DOM
     snapshot when it starts, and on a 1.166-element page that is ~1 s of
     main-thread work. Booting it during load put that work directly on top
     of the hero, and the measurements were unambiguous: across four runs of
     the same build, Clarity winning the network race and starting at ~1.0 s
     gave a Largest Contentful Paint of 3.7-4.0 s, and Clarity starting late
     gave 1.4 s. Same build, same page — only that ordering differed.

     `load` fires for every visitor, so unlike an interaction gate this loses
     no session and no visit: Clarity still records everyone. All that moves
     is the first second or so of the replay, which is the page painting.

     Bounded at both ends on purpose. `load` alone is not good enough: it is
     whenever the last straggling request finishes, and on a measured run
     here that was 10.3 s — a visitor who left at 8 s would have gone
     unrecorded, which is the very thing this is trying to avoid. So: never
     before FLOOR (the largest paint lands at ~1.4 s, so this stays out of
     its way), never after CEIL however long load takes, and normally the
     moment load says the page is done. */
  function afterLoad(fn) {
    var FLOOR = 2000;   // past the hero paint
    var CEIL = 5000;    // and never later than this, whatever load is doing
    var done = false;
    var go = function () {
      if (done) return;
      done = true;
      if (typeof window.requestIdleCallback === 'function') requestIdleCallback(fn, { timeout: 1000 });
      else setTimeout(fn, 0);
    };
    var notBeforeFloor = function () {
      var t = performance.now();
      setTimeout(go, t >= FLOOR ? 0 : FLOOR - t);
    };
    if (document.readyState === 'complete') notBeforeFloor();
    else window.addEventListener('load', notBeforeFloor, { once: true });
    setTimeout(go, CEIL);
  }

  /* ---- debug switches -------------------------------------- */
  // ?name=1 turns on for the tab, ?name=0 off. Sticky so it survives the
  // page-to-page navigation you need to actually watch a funnel.
  function flag(name) {
    var m = new RegExp('[?&]' + name + '=([01])').exec(location.search);
    try {
      if (m) {
        if (m[1] === '1') { sessionStorage.setItem('mm_' + name, '1'); return true; }
        sessionStorage.removeItem('mm_' + name); return false;
      }
      return sessionStorage.getItem('mm_' + name) === '1';
    } catch (e) { return !!m && m[1] === '1'; }   // storage blocked
  }
  var GA_DEBUG = flag('gadebug');
  var MM_DEBUG = flag('mmdebug');

  function log() {
    if (!MM_DEBUG || !window.console || !console.log) return;
    try {
      console.log.apply(console, ['%c[MM]', 'color:#B8893A;font-weight:600']
        .concat([].slice.call(arguments)));
    } catch (e) { }
  }

  /* ---- stored decision ------------------------------------ */
  function saved() {
    try {
      var raw = localStorage.getItem(KEY);
      if (!raw) return null;
      var v = JSON.parse(raw);
      if (!v || !v.state) return null;
      if (Date.now() - (v.at || 0) > TTL) return null;   // expired → ask again
      return v.state;
    } catch (e) { return null; }       // private mode / storage disabled
  }
  function remember(state) {
    try { localStorage.setItem(KEY, JSON.stringify({ state: state, at: Date.now() })); }
    catch (e) { /* storage blocked — the choice lasts this page only */ }
  }

  /* ---- tell Shopify the same thing -------------------------
     On the Shopify theme there were two cookie notices on screen at once:
     this one, and Shopify's own from Settings → Customer privacy (client
     review 27.08.2026). Only one can be shown, and this is the one that
     is branded, translated into all five languages and actually wired to
     Google, Clarity and Meta — so Shopify's is switched off in the admin
     and this banner reports the decision into Shopify's Customer Privacy
     API instead. That API is what gates Shopify's own marketing pixels
     and any app that reads consent, so skipping this step would silence
     the banner *and* the thing it is there to control.

     Everything here is defensive: window.Shopify only exists on the
     theme, loadFeatures only on a storefront page, and neither exists on
     the static site — where this quietly does nothing, as it should. */
  function tellShopify(state, fromClick) {
    var granted = state === 'granted';
    var consent = {
      analytics: granted,
      marketing: granted,
      preferences: granted,
      sale_of_data: granted
    };
    function send() {
      try {
        var cp = window.Shopify && window.Shopify.customerPrivacy;
        if (!cp || typeof cp.setTrackingConsent !== 'function') return;
        /* Shopify is explicit that consent must be recorded from a visitor
           interaction, never on their behalf. On a page load we are replaying
           a decision this visitor already made, so only write it when Shopify
           has no record of its own or is out of step — never as a routine
           overwrite of something fresher. */
        if (!fromClick) {
          try {
            var cur = typeof cp.currentVisitorConsent === 'function'
              ? cp.currentVisitorConsent() : null;
            if (cur && String(cur.marketing) === String(granted)
                    && String(cur.analytics) === String(granted)) return;
          } catch (e) { /* no record yet — fall through and write ours */ }
        }
        cp.setTrackingConsent(consent, function () {
          log('Shopify customer privacy →', granted ? 'granted' : 'denied');
        });
      } catch (e) { /* API not on this page */ }
    }
    try {
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          function (err) { if (!err) send(); }
        );
      } else { send(); }
    } catch (e) { /* not a Shopify storefront */ }
  }

  var choice = saved();
  // Shopify keeps its own record of consent and cannot read ours, so a decision
  // taken on an earlier visit has to be replayed to it on every page load.
  if (choice === 'granted' || choice === 'denied') tellShopify(choice);

  /* ---- Google: loads for everyone, denied by default ------- */
  window.dataLayer = window.dataLayer || [];
  function gtag() { dataLayer.push(arguments); }
  window.gtag = gtag;

  // Must be the very first thing in the dataLayer, before gtag.js arrives.
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'granted',   // language / cart preferences
    security_storage: 'granted',
    wait_for_update: 500                // give a stored decision time to land
  });
  gtag('set', 'ads_data_redaction', true);   // strip ad identifiers while denied
  gtag('set', 'url_passthrough', true);      // keep gclid across pages without cookies

  function grantGoogle() {
    gtag('consent', 'update', {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      analytics_storage: 'granted'
    });
    log('Google consent → granted (cookies on)');
  }

  // A stored Accept is applied before config, so the very first hit of the
  // page is already a normal, cookied one rather than an anonymous ping.
  if (choice === 'granted') grantGoogle();
  else log('Google consent → denied (cookieless pings only)');

  var googleBooted = false;
  function loadGoogle() {
    if (googleBooted) return;
    googleBooted = true;

    var s = document.createElement('script');
    s.async = true;
    s.src = 'https://www.googletagmanager.com/gtag/js?id=' + GOOGLE_TAG;
    (document.head || document.documentElement).appendChild(s);

    /* The tag is still configured — the linker below and the quote form's
       generate_lead both need it — but it must not send a page_view of its
       own. Shopify's Google channel pixel fires page_view explicitly on
       every storefront page, so leaving this true counted every view twice. */
    var cfg = {
      send_page_view: false,
      linker: { domains: LINKED_DOMAINS, accept_incoming: true }
    };
    if (GA_DEBUG) cfg.debug_mode = true;

    gtag('js', new Date());
    gtag('config', GOOGLE_TAG, cfg);   // one tag, every destination
    log('Google tag configured', GOOGLE_TAG, GA_DEBUG ? '(DebugView ON)' : '');
  }

  /* Deferred, and losslessly so — see the note beside onInteraction. The
     consent defaults and `set` calls above have already gone into the
     dataLayer, in order, and gtag.js reads them when it lands. */
  onInteraction(loadGoogle);

  /* ---- Meta: nothing at all until Accept ------------------- */
  /* Shopify's Facebook & Instagram channel initialises THIS SAME pixel in THIS
     SAME document. Verified on the live page 06.09.2026: with consent granted,
     fbevents.js was present twice and Meta logged

       [Meta Pixel] - Duplicate Pixel ID: 794784032223260

     A second fbq('init') for one id registers it twice, and every fbq('track')
     after that is at risk of being counted twice. Only `Lead` is ours, so only
     `Lead` is at risk — but a double-counted lead is a wrong number on the one
     conversion this file exists to report.

     Two changes stop it. bootMeta() no longer claims the pixel if something
     already has; and it is no longer called on Accept, but on the first Meta
     event there is to send — see whenMeta(). By then Shopify's pixel is long
     up, so the normal path injects nothing at all. If the channel is ever
     removed or blocked, nothing has claimed the pixel and we load our own,
     with nothing to collide with. */
  function pixelClaimed() {
    try {
      var byId = window.fbq && window.fbq.instance && window.fbq.instance.pixelsByID;
      if (byId) return !!byId[META_ID];
    } catch (e) { /* internal API moved — fall through */ }
    /* No way to read the registry. fbq existing at all means someone loaded
       fbevents.js, and on this storefront that someone is Shopify. Assume it
       is claimed: a Lead sent to their pixel is right, a second registration
       is not. */
    return typeof window.fbq === 'function';
  }

  function bootMeta() {
    if (metaBooted) return;
    metaBooted = true;

    if (pixelClaimed()) {
      log('Meta Pixel already initialised (Shopify) — not registering it twice');
      return;
    }

    !function (f, b, e, v, n, t, s) {
      if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
      if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
      t = b.createElement(e); t.async = !0; t.src = v;
      // Meta's stock snippet assumes a <script> already exists to insert before.
      // Fall back to <head> rather than throwing if one somehow does not.
      s = b.getElementsByTagName(e)[0];
      if (s && s.parentNode) s.parentNode.insertBefore(t, s);
      else (b.head || b.documentElement).appendChild(t);
    }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    /* Nothing else has this pixel, so it is ours to init. Still no PageView:
       the sales channel sends that on every storefront page. */
    fbq('init', META_ID);
    log('Meta Pixel booted here', META_ID, '(no Shopify pixel found)');
  }

  /* ---- Clarity: loads for everyone, cookieless until Accept - */
  function bootClarity() {
    if (clarityBooted) return;
    clarityBooted = true;

    (function (c, l, a, r, i, t, y) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
      t = l.createElement(r); t.async = 1; t.src = 'https://www.clarity.ms/tag/' + i;
      y = l.getElementsByTagName(r)[0];
      // Same guard as the Meta snippet: <head> if this page has no script tag
      // to insert before. analytics.js itself is one, so this is belt-and-braces.
      if (y && y.parentNode) y.parentNode.insertBefore(t, y);
      else (l.head || l.documentElement).appendChild(t);
    })(window, document, 'clarity', 'script', CLARITY_ID);

    /* Tell Clarity where consent stands. `false` is its cookieless mode:
       it still records, but writes no _clck/_clsk and nothing survives the
       visit. grantClarity() upgrades this when Accept is clicked. */
    try { window.clarity('consent', choice === 'granted'); } catch (e) { }
    log('Clarity booted', CLARITY_ID, choice === 'granted' ? '(cookies on)' : '(cookieless)');

    var held = clarityQueue.splice(0, clarityQueue.length);
    held.forEach(function (fn) { try { fn(); } catch (e) { } });
  }

  function grantClarity() {
    try { window.clarity('consent'); } catch (e) { }
    log('Clarity consent → granted (cookies on)');
  }

  /* Deliberately NOT bootMeta() here. Claiming the pixel during the first
     paint is what collided with Shopify's. It boots on the first event that
     needs it instead — see whenMeta(). */
  var metaAllowed = (choice === 'granted');

  /* Unconditional, and last of the three so a failure here cannot stop
     Google or Meta from starting. Still every visitor, still cookieless
     until Accept — only the moment has moved, to just after the page has
     finished loading. See afterLoad() for why that is worth ~2.6 s of
     Largest Contentful Paint and costs no session at all.
     CLARITY_ON_INTERACTION = true would delay it further, to the first
     interaction, which WOULD drop a visit that never interacts. */
  if (CLARITY_ON_INTERACTION) onInteraction(bootClarity);
  else afterLoad(bootClarity);

  /* ---- event plumbing -------------------------------------- */
  /* Run now if the visitor has said yes — booting the pixel at this moment if
     it has not been booted — otherwise hold it until they decide. */
  function whenMeta(fn) {
    if (metaAllowed) {
      bootMeta();
      try { fn(); } catch (e) { }
      return;
    }
    if (metaQueue.length < 20) metaQueue.push(fn);   // bounded: never grow unwatched
  }

  // Anything the visitor did while the banner was still up.
  function flushMeta() {
    var held = metaQueue.splice(0, metaQueue.length);
    held.forEach(whenMeta);
  }

  function whenClarity(fn) {
    if (clarityBooted) { try { fn(); } catch (e) { } return; }
    if (clarityQueue.length < 20) clarityQueue.push(fn);
  }

  // An "item" is {code, name, price, qty}. Both vendors want the same
  // facts in different shapes, so normalise once here.
  function norm(it) {
    it = it || {};
    return {
      code: String(it.code || it.id || ''),
      name: String(it.name || it.code || 'MagnuMaster'),
      price: Number(it.price) || 0,
      qty: Number(it.qty) || 1
    };
  }

  function fire(metaEvent, googleEvent, items) {
    items = (items || []).map(norm);
    var value = items.reduce(function (s, i) { return s + i.price * i.qty; }, 0);
    log('event', googleEvent, '/', metaEvent,
        '→ Clarity only; Shopify reports this one to Google and Meta',
        { value: value, items: items });

    /* Google and Meta are NOT sent from here. Shopify's sales-channel pixels
       already report view_item / add_to_cart / begin_checkout, and their Meta
       equivalents, to the same tag and the same pixel. Sending them here as
       well counted each one twice — read the note at the top of this file
       before you are tempted to put them back. `metaEvent` is kept in the
       signature because the call sites and this log line still name it. */

    // Clarity takes an event name and string tags only — no schema, no value
    // field. Tags are what its dashboard filters replays on.
    whenClarity(function () {
      try {
        clarity('event', googleEvent);
        clarity('set', 'mm_value', String(Math.round(value)));
        if (items.length) clarity('set', 'mm_model', items[0].code || items[0].name);
      } catch (e) { log('clarity blocked', e); }
    });
  }

  /* ---- advanced matching -----------------------------------------------
     Meta scores every event on how much identifying information rides with
     it ("event match quality"). An anonymous product view scores 4-6/10 and
     always will — there is no email to send. The quote form is different:
     the buyer has just typed a name, an email, a phone and a country, and
     sending none of it was leaving the one conversion we own at the bottom
     of the scale.

     Mechanics, because they are easy to get wrong:

     - Advanced matching is attached with fbq('init', id, {...}), not on the
       event. Calling init again for an id that already exists MERGES into
       that registry entry rather than adding a second sender — pixelsByID is
       keyed by id — so this does not resurrect the duplicate-pixel problem
       that bootMeta() exists to avoid. Meta still logs a "Duplicate Pixel
       ID" advisory; that line is cosmetic here.
     - We send RAW values, not our own hashes. Meta's library normalises
       (trim, lowercase, strip punctuation, phone to digits) and SHA-256
       hashes them before anything leaves the browser. Hashing them
       ourselves means guessing that normalisation, and a hash that
       normalised differently matches nobody at all — worse than sending
       nothing.
     - Nothing here runs before consent: every call site is inside
       whenMeta().

     Country is deliberately conditional. The form's country field is free
     text ("Austria", "österreich", "AT"), Meta wants a two-letter ISO code,
     and we have no name-to-code map. A wrong country cannot match and can
     only drag the score down, so it is sent only when the buyer typed
     something that already is a code we ship to. */
  function advancedMatching(u) {
    if (!u) return null;
    var am = {};
    var str = function (v) { return typeof v === 'string' ? v.trim() : ''; };

    var em = str(u.email);
    if (em.indexOf('@') > 0) am.em = em;

    // Meta wants digits with the country code. Keep the digits, drop
    // everything else; it discards a number too short to be real.
    var ph = str(u.phone).replace(/[^0-9]/g, '');
    if (ph.length >= 7) am.ph = ph;

    var name = str(u.name);
    if (name) {
      var bits = name.split(/\s+/);
      if (bits[0]) am.fn = bits[0];
      if (bits.length > 1) am.ln = bits[bits.length - 1];
    }

    var c = str(u.country).toUpperCase();
    if (/^[A-Z]{2}$/.test(c)) {
      var ships = (window.MM_SHOPIFY && window.MM_SHOPIFY.shipsTo) || [];
      if (!ships.length || ships.indexOf(c) !== -1) am.country = c.toLowerCase();
    }

    return Object.keys(am).length ? am : null;
  }

  /* One Contact per channel per session — see MM_TRACK.contact. Returns the
     session's event id for that channel and whether it was already sent.
     sessionStorage, so it ends with the tab; if storage is blocked the
     in-memory copy still stops a double-tap on this page. */
  var contactMemo = {};
  function contactSeen(method) {
    var key = 'mm_contact_' + method;
    var id = contactMemo[key];
    try { id = id || sessionStorage.getItem(key); } catch (e) { }
    if (id) return { already: true, id: id };
    id = 'contact-' + method + '-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
    contactMemo[key] = id;
    try { sessionStorage.setItem(key, id); } catch (e) { }
    return { already: false, id: id };
  }

  window.MM_TRACK = {
    viewContent: function (item) { fire('ViewContent', 'view_item', [item]); },
    addToCart: function (item) { fire('AddToCart', 'add_to_cart', [item]); },
    beginCheckout: function (items) { fire('InitiateCheckout', 'begin_checkout', items); },

    // Lead carries no product value — it's an enquiry, not a sale.
    /* detail.user — {name, email, phone, country} straight off the quote
       form — is optional. Without it this behaves exactly as before. */
    lead: function (detail) {
      detail = detail || {};
      // Never print the buyer's details to the console, not even under
      // ?mmdebug=1. Say which fields we have, not what is in them.
      var am = advancedMatching(detail.user);
      log('event generate_lead / Lead',
          { name: detail.name, model: detail.model },
          am ? 'with advanced matching: ' + Object.keys(am).join(', ') : 'no advanced matching');
      // A submit is an interaction, so gtag.js is normally already on its
      // way. Ask anyway: a form submitted from script would not have woken
      // it, and this is the one event this container exists to send.
      loadGoogle();
      try {
        gtag('event', 'generate_lead', {
          currency: CUR, value: 0,
          form_name: detail.name || 'Quote request',
          model: detail.model || 'unspecified'
        });
      } catch (e) { log('gtag blocked', e); }
      whenMeta(function () {
        try {
          // Attach what the buyer just told us, then send to THIS pixel only.
          // Plain fbq('track') fires on every initialised pixel id, so if an
          // app ever adds a second one our Lead would leak onto it too.
          if (am) fbq('init', META_ID, am);
          fbq('trackSingle', META_ID, 'Lead', {
            content_name: detail.name || 'Quote request',
            content_category: detail.model || 'unspecified'
          });
        } catch (e) { log('fbq blocked', e); }
      });
      whenClarity(function () {
        try {
          clarity('event', 'generate_lead');
          clarity('set', 'mm_model', detail.model || 'unspecified');
        } catch (e) { log('clarity blocked', e); }
      });
    },

    /* A WhatsApp chat or a phone call started from the site. Like Lead, this
       is something Shopify cannot see, so it is ours to send, and it is
       browser-only: there is no server copy.

       Counted ONCE per channel per browser session (client review
       13.10.2026: Meta showed several Contact events for one chat). A
       visitor who taps WhatsApp, comes back and taps it again — or double-
       taps — has started one conversation, not three. Google and Meta both
       get the first tap only; Clarity still gets every tap, because there it
       marks a moment in a replay rather than a conversion. The Meta event
       also carries a stable eventID for the session, so should a repeat ever
       get through (storage blocked, a second tab) Meta folds it into the
       first instead of counting it again.

       Meta gets its standard `Contact` event, which Ads can optimise for as
       it stands. Google gets one event per channel rather than a shared
       `contact` with a parameter, because a GA4 key event — and the Ads
       conversion imported from it — is chosen by event NAME: whatsapp_click
       can be marked as a conversion without also counting every tap on the
       phone number. */
    contact: function (method) {
      method = method === 'phone' ? 'phone' : 'whatsapp';
      var googleEvent = method + '_click';
      var page = location.pathname;
      whenClarity(function () {
        try { clarity('event', googleEvent); } catch (e) { log('clarity blocked', e); }
      });
      var seen = contactSeen(method);
      if (seen.already) { log('event ' + googleEvent + ' — already counted this session'); return; }
      log('event ' + googleEvent + ' / Contact', page, seen.id);
      loadGoogle();
      try {
        gtag('event', googleEvent, { method: method, page_path: page });
      } catch (e) { log('gtag blocked', e); }
      whenMeta(function () {
        try {
          fbq('trackSingle', META_ID, 'Contact', {
            content_name: method === 'phone' ? 'Phone' : 'WhatsApp',
            content_category: page
          }, { eventID: seen.id });
        } catch (e) { log('fbq blocked', e); }
      });
    }
  };

  /* Every WhatsApp and phone link, wherever it is — the fixed buttons in
     theme.liquid, the footer, a number typed into a page in the editor.
     Delegated from the document, so it needs no markup of its own and still
     sees a link added after load. Capture phase, so a handler further down
     that stops propagation cannot hide the click from us. The link itself is
     never held up: both open somewhere else (a new tab, the dialler), so the
     page — and the queued events — are still here afterwards. */
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest && e.target.closest('a[href]');
    if (!a) return;
    var href = a.getAttribute('href') || '';
    if (/^https?:\/\/(wa\.me|api\.whatsapp\.com|(www\.)?whatsapp\.com)\//i.test(href)) window.MM_TRACK.contact('whatsapp');
    else if (/^tel:/i.test(href)) window.MM_TRACK.contact('phone');
  }, true);

  /* ---- the banner ------------------------------------------ */
  /* The banner is built before main.js has fetched a dictionary (consent has to
     be asked as early as possible), so it carries its own copy of the seven
     languages rather than waiting on MM_I18N. */
  var COPY = {
    en: { body:'We use analytics and marketing cookies to improve our shop. They are only loaded with your consent.',
          accept:'Accept all', decline:'Essential only', more:'Privacy policy', aria:'Cookie notice' },
    de: { body:'Wir verwenden Cookies für Analyse und Marketing, um unseren Shop zu verbessern. Diese werden nur mit Ihrer Zustimmung geladen.',
          accept:'Alle akzeptieren', decline:'Nur notwendige', more:'Datenschutz', aria:'Cookie-Hinweis' },
    it: { body:'Utilizziamo cookie di analisi e marketing per migliorare il nostro shop. Vengono caricati solo con il suo consenso.',
          accept:'Accetta tutti', decline:'Solo necessari', more:'Privacy', aria:'Informativa cookie' },
    es: { body:'Utilizamos cookies de análisis y marketing para mejorar nuestra tienda. Solo se cargan con su consentimiento.',
          accept:'Aceptar todo', decline:'Solo esenciales', more:'Privacidad', aria:'Aviso de cookies' },
    fr: { body:"Nous utilisons des cookies d'analyse et de marketing pour améliorer notre boutique. Ils ne sont chargés qu'avec votre consentement.",
          accept:'Tout accepter', decline:'Essentiels uniquement', more:'Confidentialité', aria:'Avis sur les cookies' },
    pt: { body:'Utilizamos cookies de análise e de marketing para melhorar a nossa loja. Só são carregados com o seu consentimento.',
          accept:'Aceitar todos', decline:'Apenas essenciais', more:'Privacidade', aria:'Aviso de cookies' },
    nl: { body:'Wij gebruiken analytische en marketingcookies om onze winkel te verbeteren. Ze worden alleen met uw toestemming geladen.',
          accept:'Alles accepteren', decline:'Alleen noodzakelijke', more:'Privacybeleid', aria:'Cookiemelding' }
  };
  var LANG = (function () {
    var l; try { l = localStorage.getItem('mm_lang'); } catch (e) {}
    if (!COPY[l]) l = (document.documentElement.lang || '').slice(0, 2).toLowerCase();
    if (!COPY[l]) l = (navigator.language || 'en').slice(0, 2).toLowerCase();
    return COPY[l] ? l : 'en';
  })();
  var T = COPY[LANG];

  var bannerEl = null;

  function render(el, T) {
    el.setAttribute('aria-label', T.aria);
    el.innerHTML =
      '<p class="cookie__txt">' + T.body +
      ' <a href="/pages/privacy">' + T.more + '</a></p>' +
      '<div class="cookie__btns">' +
      '<button type="button" class="cookie__btn" data-consent="denied">' + T.decline + '</button>' +
      '<button type="button" class="cookie__btn cookie__btn--yes" data-consent="granted">' + T.accept + '</button>' +
      '</div>';
  }

  function banner() {
    var el = document.createElement('div');
    bannerEl = el;
    el.className = 'cookie';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-live', 'polite');
    render(el, T);

    el.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-consent]');
      if (!btn) return;
      var state = btn.getAttribute('data-consent');
      remember(state);
      tellShopify(state, true);
      log('banner →', state);
      // Clarity is already running either way — Accept only upgrades it.
      if (state === 'granted') { grantGoogle(); grantClarity(); metaAllowed = true; flushMeta(); }
      else metaQueue.length = 0;      // Google and Clarity stay cookieless
      el.remove();
      // The language notice docks in the same corner and waits for this.
      document.body.dispatchEvent(new CustomEvent('mm:consent-closed'));
    });

    document.body.appendChild(el);
    requestAnimationFrame(function () { el.classList.add('show'); });
  }

  if (choice !== 'granted' && choice !== 'denied') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', banner);
    else banner();
  }

  // Lets the privacy page offer a "change your choice" link.
  window.MM_CONSENT = {
    reset: function () { try { localStorage.removeItem(KEY); } catch (e) { } location.reload(); },
    // The banner has to paint before the country lookup returns, so it can end
    // up a language behind the page. main.js calls this once the two agree.
    relang: function (l) {
      if (bannerEl && bannerEl.isConnected && COPY[l]) render(bannerEl, COPY[l]);
    },
    // One-line health check for the console — "is tracking actually on?"
    status: function () {
      return {
        consent: saved() || 'undecided',
        // `gtag` is our own dataLayer shim and exists from the first line, so
        // it says nothing about whether the library arrived. googleBooted does.
        googleTag: GOOGLE_TAG, googleLoaded: googleBooted,
        metaId: META_ID,
        // Did THIS file boot the pixel? Normally false — Shopify's channel
        // gets there first and we deliberately do not register a second time.
        metaLoaded: metaBooted && typeof window.fbq === 'function',
        // Is a pixel live on the page at all, from either source? This is the
        // one to read when asking "is Meta tracking running?".
        metaPixelPresent: pixelClaimed(),
        clarityId: CLARITY_ID, clarityLoaded: clarityBooted && typeof window.clarity === 'function',
        // Google and Clarity wait for this; until it flips, neither is fetched.
        interacted: interacted,
        clarityOnInteraction: CLARITY_ON_INTERACTION,
        gaDebug: GA_DEBUG, mmDebug: MM_DEBUG,
        queuedMetaEvents: metaQueue.length,
        queuedClarityEvents: clarityQueue.length
      };
    }
  };

  log('status', window.MM_CONSENT.status());
})();
